import { FormControl, Grid, InputLabel, MenuItem, TextField, Typography } from '@mui/material'
import React, { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { languages } from '../../../../../Types/General';
import { useAppDispatch, useAppSelector } from '../../../../../Redux/store';
import { setCompany, setCountry, setNextDisabled, setSummary, setPrimaryLanguage, setSecondaryLanguage } from '../../Slices/ClientProfileCreationSlice';
import { useUser } from '../../../../../Hooks/useUser';

interface BusinessInformationForm {
    Company: string;
    Country: string;
    Summary: string;
    PrimaryLanguage: string;
    SecondaryLanguage: string;
}


export const BusinessInformation = () => {
    /* ******************** Hooks ******************** */
    const dispatch = useAppDispatch();
    const { user } = useUser();
    const profileCreation = useAppSelector( state => state.ClientProfileCreation);
    const {
        control,
        formState: {errors, dirtyFields},
        getValues,
        watch
    } = useForm<BusinessInformationForm>({
        defaultValues: {
            Company: profileCreation.company || user?.company || '',
            Country: profileCreation.country || user?.country || '',
            //@ts-ignore
            Summary: profileCreation.summary || user?.description || '',
            //@ts-ignore
            PrimaryLanguage: profileCreation.primaryLanguage || user?.languages[0] || '',
            //@ts-ignore
            SecondaryLanguage: profileCreation.secondaryLanguage || user?.languages[1] || ''
        }
    })
    /* ******************** Variables ******************** */
    const watchFields = watch(['Company', 'Country', 'Summary', 'PrimaryLanguage'])
    /* ******************** Functions ******************** */
    /* ******************** Effects ******************** */
    useEffect(()=>{
        if (watchFields.every( field => field.length > 0) && profileCreation.nextDisabled) {
            dispatch(setNextDisabled(false));
        } 
        if (!watchFields.every( field => field.length > 0) && !profileCreation.nextDisabled) {
            dispatch(setNextDisabled(true));
        } 
    }, [watchFields])

    useEffect(()=>{
        return function cleanup() {
            if (dirtyFields.Company) dispatch(setCompany(getValues('Company')))
            if (dirtyFields.Country) dispatch(setCountry(getValues('Country')))
            if (dirtyFields.Summary) dispatch(setSummary(getValues('Summary')))
            if (dirtyFields.PrimaryLanguage) dispatch(setPrimaryLanguage(getValues('PrimaryLanguage')))
            if (dirtyFields.SecondaryLanguage) dispatch(setSecondaryLanguage(getValues('SecondaryLanguage')))
        }
    }, [dirtyFields])
    /* ******************** JSX ******************** */
    return (
        <Grid container gap={'20px'}>
            <Typography variant='lora-title-large' color={'primary'} fontWeight={'bold'}>Add your business information</Typography> 
            <Grid item display={"flex"} flexDirection={"row"} gap={"12px"}>
                <Grid item display={"flex"} flexDirection={"column"}>
                    <Typography variant='body-small' color={'black.200'} marginBottom={1}>Company</Typography>  
                    <Controller
                        name="Company"
                        control={control}
                        rules={{required: true}}
                        render={({ field }) =>( 
                            <FormControl>   
                                <InputLabel sx={{opacity: 1}}>{field.value ?"":"Name"}</InputLabel>                   
                                <TextField
                                    {...field}
                                    // error={hasErrors(field.name, errors)}
                                    sx={{
                                        width: "270px"
                                    }}
                                    //onChange={(params: any) => handleCompanyChange(params.target.value)}
                                />
                            </FormControl>
                        )}
                    />   
                </Grid>
                <Grid item display={"flex"} flexDirection={"column"}>
                    <Typography variant='body-small' color={'black.200'} marginBottom={1}>Country</Typography>
                    <Controller
                        name="Country"
                        control={control}
                        render={({ field }) =>( 
                            <FormControl>   
                                <InputLabel sx={{opacity: 1}}>{field.value ?"":"City, Country"}</InputLabel>                   
                                <TextField
                                    {...field}
                                    // error={hasErrors(field.name, errors)}
                                    sx={{
                                        width: "270px"
                                    }}
                                    //onChange={(params: any) => handleLocationChange(params.target.value)}
                                />
                            </FormControl>
                        )}
                    />  
                </Grid>
            </Grid>
            <Grid item display={"flex"} flexDirection={"row"} gap={"12px"}>
                <Grid item display={"flex"} flexDirection={"column"}>
                    <Typography variant='body-small' color={'black.200'} marginBottom={1}>Primary language</Typography>  
                    <Controller
                        name="PrimaryLanguage"
                        control={control}
                        rules={{required: true}}
                        render={({ field }) =>( 
                            <FormControl>   
                                <InputLabel sx={{opacity: 1}}>{field.value ?"":"Select language"}</InputLabel>                   
                                <TextField
                                    {...field}
                                    select
                                    // error={hasErrors(field.name, errors)}
                                    sx={{
                                        width: "270px"
                                    }}
                                    //onChange={(params: any) => handlePrimaryChange(params.target.value)}
                                >
                                    {
                                        languages.map((language) => (
                                            <MenuItem key={language} value={language}>
                                                {language}
                                            </MenuItem>
                                        ))
                                    }
                                </TextField>
                            </FormControl>
                        )}
                    />   
                </Grid>
                <Grid item display={"flex"} flexDirection={"column"}>
                    <Typography variant='body-small' color={'black.200'} marginBottom={1}>Secondary language (optional)</Typography>
                    <Controller
                        name="SecondaryLanguage"
                        control={control}
                        render={({ field }) =>( 
                            <FormControl>   
                                <InputLabel sx={{opacity: 1}}>{field.value ?"":"Select language"}</InputLabel>                   
                                <TextField
                                    {...field}
                                    select
                                    // error={hasErrors(field.name, errors)}
                                    sx={{
                                        width: "270px"
                                    }}
                                    //onChange={(params: any) => handleSecondaryChange(params.target.value)}
                                >
                                    {
                                        languages.map((language) => (
                                            <MenuItem key={language} value={language}>
                                                {language}
                                            </MenuItem>
                                        ))
                                    }
                                </TextField>
                            </FormControl>
                        )}
                    />  
                </Grid>
            </Grid>
            <Grid item xs display={"flex"} flexDirection={"column"}>
                    <Typography variant='body-small' color={'black.200'} marginBottom={1}>About me</Typography>
                    <Controller
                        name="Summary"
                        control={control}
                        rules={{required: true}}
                        render={({ field }) =>(            
                            <TextField
                                {...field}
                                placeholder='Add a short description about you or your company'
                                fullWidth
                                multiline
                                rows={5}
                                // error={hasErrors(field.name, errors)}
                                sx={{
                                    width: "100%",
                                    '& .MuiInputBase-input': {
                                        maxHeight: '126px'
                                    }
                                }}
                                //onChange={(params: any) => handleSummaryChange(params.target.value)}
                            />
                        
                        )}
                    />  
            </Grid>
        </Grid>
    )
}